import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toInteger } from 'lodash';
import {
  FilterTypes,
  INTIAL_FILTERS,
  RANGE_FILTERS,
  SINGLE_FILTERS,
} from '../constants/initialFilters';
import { filterCurrentState } from '../utils/analyticsEventListeners';
import { RootState } from '.';
import { NonAudiCountType } from '../types';
import buildQueryParams from '../hooks/Filters/useUpdateQueryParams';
import { BODY_FILTERS_LIST_NEW } from '../constants/listOfFilters';

interface FiltersReducerTypes {
  confirmedFilters: FilterTypes;
  availableFilters: any;
  nonAudiCounts: {
    nonAudiManufactures: NonAudiCountType[] | [];
    nonAudiModels: NonAudiCountType[] | [];
  };
}

type SigleFilterAddActionType = {
  [key: string]: string;
};

type RangeFilterAddActionType = {
  [key: string]: number[] | string;
};

const initialState: FiltersReducerTypes = {
  confirmedFilters: INTIAL_FILTERS,
  availableFilters: {},
  nonAudiCounts: {
    nonAudiManufactures: [],
    nonAudiModels: [],
  },
};

const filterSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    // Initially set filters from LS to Redux
    setFiltersFromLS: (state: FiltersReducerTypes, action: PayloadAction<FilterTypes>) => {
      return { ...state, confirmedFilters: action.payload };
    },

    setNonAudiCounts: (state: FiltersReducerTypes, action) => {
      return { ...state, nonAudiCounts: action.payload };
    },

    // Initially set available filters from LS to Redux
    setAvailableFilters: (state, action) => {
      return { ...state, availableFilters: action.payload };
    },

    setMainDealerId: (state, action) => {
      const mainDealerId = action.payload;
      const newState = {
        ...state,
        confirmedFilters: {
          ...state.confirmedFilters,
          partnerDealers: [mainDealerId],
        },
      };
      return newState;
    },

    changeRangeFilter: (state, action: PayloadAction<RangeFilterAddActionType>) => {
      const filterType = Object.keys(action.payload)[0]; // Access the first key (filterType)
      const filterValue = action.payload[filterType]; // Access the value associated with filterType
      const { isUrlParamsDynamic, isTier3page } = action.payload;
      if (filterType === 'power' || filterType === 'mileage' || filterType === 'price') {
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            [filterType]: filterValue,
          },
        };
        if (isTier3page !== 'true') {
          localStorage.setItem('selectedFilters-v5', JSON.stringify(newState.confirmedFilters));
        }
        if (isUrlParamsDynamic === 'true') {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }
      return { ...state };
    },

    // Remove temp filter from filter menu
    addOrRemoveFilter: (state, action: PayloadAction<SigleFilterAddActionType>) => {
      const { filterType, filterValue, isUrlParamsDynamic, isTier3page } = action.payload;
      const filterArray = (state.confirmedFilters as any)[filterType];

      if (filterType === 'otherMakes' && filterArray.includes(filterValue)) {
        const convertedModelsToMakes = state.confirmedFilters.nonAudiModels.filter(
          (model) => model.slice(0, 2) !== filterValue,
        );
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            nonAudiModels: convertedModelsToMakes,
            [filterType]: (state.confirmedFilters as any)[filterType].filter(
              (item: string) => item !== filterValue,
            ),
          },
        };
        if (isUrlParamsDynamic === 'true') {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }

      if (filterType === 'nonAudi') {
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            nonAudi: filterValue,
            usedInventoryType: 'has-warranties.nonAudi',
          },
        };
        if (isUrlParamsDynamic === 'true') {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }

      if (
        filterType === 'vehicleType' ||
        filterType === 'usedInventoryType' ||
        filterType === 'availableInMonths'
      ) {
        // Check if we switched between new or used filters
        // Then remove all the selected filters, except dealers for T3
        const wasFilterChanged =
          filterType === 'vehicleType' && (filterValue === 'new' || filterValue === 'used');

        const updatedState = wasFilterChanged
          ? { ...INTIAL_FILTERS, partnerDealers: [...state.confirmedFilters.partnerDealers] }
          : state.confirmedFilters;
        const newState = {
          ...state,
          confirmedFilters: {
            ...updatedState,
            [filterType]: filterType === 'availableInMonths' ? toInteger(filterValue) : filterValue,
            nonAudi: '',
          },
        };
        if (isTier3page !== 'true') {
          localStorage.setItem('selectedFilters-v5', JSON.stringify(newState.confirmedFilters));
        }
        if (isUrlParamsDynamic === 'true') {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }

      if (filterArray.includes(filterValue)) {
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            [filterType]: (state.confirmedFilters as any)[filterType].filter(
              (item: string) => item !== filterValue,
            ),
          },
        };
        if (isTier3page !== 'true') {
          localStorage.setItem('selectedFilters-v5', JSON.stringify(newState.confirmedFilters));
        }
        if (isUrlParamsDynamic === 'true') {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }
      const newState = {
        ...state,
        confirmedFilters: {
          ...state.confirmedFilters,
          [filterType]: [...(state.confirmedFilters as any)[filterType], filterValue],
        },
      };
      if (isTier3page !== 'true') {
        localStorage.setItem('selectedFilters-v5', JSON.stringify(newState.confirmedFilters));
      }
      if (isUrlParamsDynamic === 'true') {
        buildQueryParams(newState.confirmedFilters);
      }
      return newState;
    },

    // Remove filter from everywhere (filter tags and filter menu) + upload fresh filters to LS
    removeFilter: (state, action) => {
      const { filterType, filterValue, isTier3page, isUrlParamsDynamic } = action.payload;
      if (isTier3page === 'true' && filterType === 'nonAudi') {
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            usedInventoryType: '',
            [filterType]: filterValue,
          },
        };
        if (isTier3page === 'true' && isUrlParamsDynamic === 'true') {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }
      if (SINGLE_FILTERS.includes(filterType)) {
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            [filterType]: filterValue,
          },
        };
        if (isTier3page !== 'true') {
          localStorage.setItem('selectedFilters-v5', JSON.stringify(newState.confirmedFilters));
        }
        if (isTier3page === 'true' && isUrlParamsDynamic === 'true') {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }
      if (RANGE_FILTERS.includes(filterType)) {
        const newState = {
          ...state,
          confirmedFilters: {
            ...state.confirmedFilters,
            [filterType]: [],
          },
        };
        if (isTier3page !== 'true') {
          localStorage.setItem('selectedFilters-v5', JSON.stringify(newState.confirmedFilters));
        }
        if (isTier3page === 'true' && isUrlParamsDynamic === 'true') {
          buildQueryParams(newState.confirmedFilters);
        }
        return newState;
      }
      const newState = {
        ...state,
        confirmedFilters: {
          ...state.confirmedFilters,
          [filterType]: (state.confirmedFilters as any)[filterType].filter(
            (item: string) => item !== filterValue,
          ),
        },
      };
      // call filter current state(Data Layer events) when a filter gets removed from filters
      filterCurrentState(newState.confirmedFilters);
      if (isTier3page !== 'true') {
        localStorage.setItem('selectedFilters-v5', JSON.stringify(newState.confirmedFilters));
      }
      if (isTier3page === 'true' && isUrlParamsDynamic === 'true') {
        buildQueryParams(newState.confirmedFilters);
      }
      return newState;
    },

    // Apply filter from filters menu
    setConfirmaedFilters: (state, action) => {
      // Do not add partnerDealers to localStorage
      const { temporaryFilters, isTier3page } = action.payload;
      if (isTier3page !== 'true') {
        localStorage.setItem('selectedFilters-v5', JSON.stringify(temporaryFilters));
      }
      return { ...state, confirmedFilters: temporaryFilters };
    },

    // Remove ALL filters from everywhere and set initial ones.
    clearAllFilters: (state) => {
      // Do not add partnerDealers to localStorage
      const { ...payloadForLS } = INTIAL_FILTERS;
      localStorage.setItem('selectedFilters-v5', JSON.stringify(payloadForLS));
      return { ...state, confirmedFilters: INTIAL_FILTERS, temporaryFilters: INTIAL_FILTERS };
    },

    setDefaultTier3Filters: (state, action) => {
      return { ...state, confirmedFilters: action.payload, temporaryFilters: action.payload };
    },
  },
});

export const selectFilters = (state: RootState) => state.filters;

export const {
  setFiltersFromLS,
  setNonAudiCounts,
  setAvailableFilters,
  addOrRemoveFilter,
  changeRangeFilter,
  removeFilter,
  setConfirmaedFilters,
  clearAllFilters,
  setMainDealerId,
  setDefaultTier3Filters,
} = filterSlice.actions;

export default filterSlice.reducer;
